import { getDocs, query, where } from 'firebase/firestore'

// Firestore caps an `in` filter at 30 values. Seller-side views match on
// "this account plus every store it owns", so an owner with 30 or more
// stores would blow that cap and the query would throw.
//
// The cap is per query, not fundamental: the SDK already runs an `in` filter
// as parallel queries under the hood, so this does the same thing explicitly
// once the list is longer. Reads are unchanged -- the same documents match
// either way -- and the chunks run concurrently.
export const FIRESTORE_IN_LIMIT = 30

export const chunkValues = (values = [], size = FIRESTORE_IN_LIMIT) => {
    const chunks = []
    for (let i = 0; i < values.length; i += size) {
        chunks.push(values.slice(i, i + size))
    }
    return chunks
}

/**
 * Runs `where(field, 'in', values)` across as many values as needed and
 * returns the merged document snapshots.
 *
 * Chunks are disjoint sets of values for a single field, so a document can
 * match at most one of them -- no de-duplication is needed.
 *
 * Note this cannot paginate server-side across chunks. Every caller here
 * already fetches the full result set and sorts in memory, so that is no
 * regression; a future paginated seller dashboard would need a different
 * shape (see the note in the multi-store design).
 */
export const getDocsWhereIn = async (collectionRef, field, values, ...constraints) => {
    if (!Array.isArray(values) || values.length === 0) {
        return []
    }

    const snapshots = await Promise.all(
        chunkValues(values).map((group) =>
            getDocs(query(collectionRef, where(field, 'in', group), ...constraints))
        )
    )

    const docs = []
    for (const snapshot of snapshots) {
        snapshot.forEach((doc) => docs.push(doc))
    }
    return docs
}
